import { useCallback, RefObject } from "react";
import { AgGridReact } from "ag-grid-react";
import useUiuxStore from "src/store/useUiuxStore";
import { focusToCell } from "src/utils/grid/GridHandlerContent";
import { hasTQC1040_R01Changes, TQC1040_R01Dto } from "../Dto_TQC1040_R01";

interface UseTQC1040_R01ModalProps {
	updateTQC1040_R01List: (index: number, data: TQC1040_R01Dto) => void;
	originalTQC1040_R01List: TQC1040_R01Dto[];
	gridRef: RefObject<AgGridReact<TQC1040_R01Dto> | null>;
}

/**
 * 검사자(inspectEmpno) 코드 검색 팝업을 열고, 선택/초기화 결과를 해당 행에 반영하는 훅.
 * 사원 검색 카테고리('검사자')로 팝업을 띄우고, 확정 시 사번(empNo)과 사원명(empNm)을 함께 채운다.
 *
 * @param updateTQC1040_R01List 변경된 행을 valueIndex 로 갱신하는 스토어 액션
 * @param gridRef 팝업 종료 시 편집 모드를 정리하기 위한 그리드 ref
 */
export function useTQC1040_R01Modal({
	updateTQC1040_R01List,
	originalTQC1040_R01List,
	gridRef,
}: UseTQC1040_R01ModalProps) {
	const { setCodeSearchModalProps } = useUiuxStore();

	const handlePopupClose = useCallback(() => {
		if (gridRef.current?.api) {
			gridRef.current.api.stopEditing();
		}
	}, [gridRef]);

	const settingCodeSearchModalProps = useCallback(
		(
			field: "inspectEmpno" | "approvorEmpno",
			search: string,
			isInfo: boolean,
			handleFoucsId: string,
			rowData: TQC1040_R01Dto,
			valueIndex: number,
		) => {
			const isInspector = field === "inspectEmpno";
			const nameField = isInspector ? "inspectEmpNm" : "approvorEmpNm";
			const updateEmployee = (empNo: string, empNm: string) => {
				const updatedRow = { ...rowData, [field]: empNo, [nameField]: empNm };
				const originalRow = originalTQC1040_R01List.find(
					(item) => item.valueIndex === valueIndex,
				);
				updateTQC1040_R01List(valueIndex, {
					...updatedRow,
					isModified: originalRow ? hasTQC1040_R01Changes(updatedRow, originalRow) : true,
				});
			};
			setCodeSearchModalProps({
				category: isInspector ? "검사자" : "승인자",
				search,
				formName: "TQC1040_R01",
				isInfo,
				handleFoucsId,
				wrapperOption: { size: "md", width: 50, overlay: true },
				onClear: () => {
					updateEmployee("", "");
					handlePopupClose();
				},
				onConfirm: (data: { empNo?: string; empNm?: string }) => {
					updateEmployee(data.empNo || "", data.empNm || "");

					setTimeout(() => {
						if (!gridRef.current) return;
						focusToCell(gridRef, valueIndex, "remark");
					}, 100);
				},
			});
		},
		[setCodeSearchModalProps, updateTQC1040_R01List, originalTQC1040_R01List, gridRef, handlePopupClose],
	);

	return { settingCodeSearchModalProps, handlePopupClose };
}
